import { supabase } from '../lib/supabase'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || ''
import {
  handleQuery,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent,
  applyFilters,
  applyOrdering,
  applyRange
} from './utils'

const TABLE_NAME = 'presentations'

const ALLOWED_ROLES = ['agent', 'admin']

function requireAgentOrAdmin(role) {
  if (!role || !ALLOWED_ROLES.includes(role)) {
    return accessDeniedResponse('Only agents and admins can access presentations.')
  }
  return null
}

/**
 * List presentations. Agents see own + admin-created; admins see all. RLS enforces.
 * @param {{
 *   role?: string,
 *   userId?: string,
 *   eventId?: string,
 *   standaloneOnly?: boolean,
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listPresentations = async (options = {}) => {
  const {
    role,
    userId,
    eventId,
    standaloneOnly,
    filters,
    orderBy = { column: 'updated_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const err = requireAgentOrAdmin(roleContext.role)
  if (err) return err

  let query = supabase.from(TABLE_NAME).select('*', { count: 'exact' })

  if (eventId !== undefined && eventId !== null) {
    query = query.eq('event_id', eventId)
  }
  if (standaloneOnly === true) {
    query = query.is('event_id', null)
  }

  query = applyFilters(query, filters || [])
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  return handleQuery(query, { fallbackMessage: 'Unable to load presentations.' })
}

/**
 * Get a single presentation by id. RLS restricts to agent (own or admin-created) or admin.
 */
export const getPresentationById = async (presentationId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const err = requireAgentOrAdmin(roleContext.role)
  if (err) return err

  const query = supabase.from(TABLE_NAME).select('*').eq('id', presentationId).maybeSingle()
  return handleQuery(query, {
    fallbackMessage: 'Unable to load the presentation.',
    allowEmpty: false
  })
}

/**
 * Create a presentation. created_by is required by the DB; agents must use own id, admins default to current user.
 */
export const createPresentation = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const err = requireAgentOrAdmin(roleContext.role)
  if (err) return err

  // Table has created_by NOT NULL. Agents must use own id (RLS); admins can set any, default to current user.
  let createdBy = payload.created_by ?? roleContext.userId
  if (createdBy == null) {
    const { data: { session } } = await supabase.auth.getSession()
    createdBy = session?.user?.id ?? null
  }
  if (createdBy == null) {
    return { data: null, error: { message: 'User context is required to create a presentation.', code: 'access_denied' } }
  }
  payload = { ...payload, created_by: createdBy }

  if (payload.event_id) {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const query = supabase.from(TABLE_NAME).insert(payload).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to create the presentation.',
    allowEmpty: false
  })
}

/**
 * Update a presentation. RLS: agent own only, admin all.
 */
export const updatePresentation = async (presentationId, updates, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const err = requireAgentOrAdmin(roleContext.role)
  if (err) return err

  if (updates.event_id != null) {
    const access = await ensureCanManageEvent(updates.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const query = supabase.from(TABLE_NAME).update(updates).eq('id', presentationId).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to update the presentation.',
    allowEmpty: false
  })
}

/**
 * Delete a presentation. RLS: agent own only, admin all.
 */
export const deletePresentation = async (presentationId, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const err = requireAgentOrAdmin(roleContext.role)
  if (err) return err

  const query = supabase.from(TABLE_NAME).delete().eq('id', presentationId).select('*').single()
  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the presentation.',
    allowEmpty: false
  })
}

/**
 * Publish or unpublish a presentation (set is_published).
 */
export const publishPresentation = async (presentationId, isPublished, options = {}) => {
  return updatePresentation(presentationId, { is_published: !!isPublished }, options)
}

/**
 * Upload a file to Publit.io via Edge Function. Returns { url, thumbnail_url } or error.
 * @param {string} presentationId
 * @param {File} file
 * @returns {Promise<{ data?: { url: string, thumbnail_url?: string }, error?: { message: string } }>}
 */
export const uploadPresentationAsset = async (presentationId, file) => {
  if (!presentationId || !file) {
    return { data: null, error: { message: 'Presentation id and file are required.' } }
  }

  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) {
    return { data: null, error: { message: 'You must be signed in to upload.' } }
  }

  const formData = new FormData()
  formData.set('file', file)
  formData.set('presentation_id', presentationId)

  const res = await fetch(`${SUPABASE_URL}/functions/v1/upload-presentation-asset`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: formData
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    return { data: null, error: { message: json?.error || 'Upload failed.' } }
  }
  if (!json?.url) {
    return { data: null, error: { message: json?.error || 'No URL returned.' } }
  }
  return { data: { url: json.url, thumbnail_url: json.thumbnail_url || json.url }, error: null }
}
